import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EyeOutlined, ReloadOutlined, RobotOutlined, SendOutlined, StopOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';

import { Bento, DataTable, MenuThaoTac, PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { KhongCoQuyen, LoiRpa } from '../components/chung';
import { FormGiaoViec } from '../components/agent/FormGiaoViec';
import { HopChiTietTask, HopHuyTask } from '../components/agent/HopChiTietTask';
import { useThoiLuong } from '../components/agent/thoi-luong';
import { GATEWAY_HIEN, QUYEN, TRANG_THAI_TASK } from '../constants';
import { useTaskAgent, useTrangThaiAgent } from '../hooks/useAgent';
import { KHOA_RPA } from '../hooks/useRpa';
import { danhSachAgent, tinhTrangGateway } from '../nghiep-vu/agent';
import type { TaskAgent, TrangThaiAgent } from '../types';

/**
 * Trợ lý Agent RPA (`/rpa/agent`, `RpaAgentView` của native): giao việc cho agent (Hermes / OpenClaw) qua Agent Gateway;
 * máy chủ gom lượt chạy, sự cố, Workflow từ OpenCore gửi kèm; agent trả báo cáo và hành động ĐỀ XUẤT — chỉ chạy khi
 * người có `rpa:agent:execute` xác nhận từng hành động. Xem `rpa:agent:read`, giao và huỷ `rpa:agent:create`.
 */
export function AgentView() {
  const { t } = useT();
  const qc = useQueryClient();
  const quyen = useModulePermissions(QUYEN);
  const tieuDe = t('nav.muc.troLyAgentRpa');

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: tieuDe }]}
        titleIcon={<Sparkles size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaAgent.moTa')}
        actions={
          quyen.agentDoc ? (
            <Button variant="outline" size="md" icon={<ReloadOutlined />} onClick={() => void qc.invalidateQueries({ queryKey: KHOA_RPA.agent })}>
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />
      {quyen.agentDoc ? (
        <NoiDung giaoDuoc={quyen.agentTao} chayDuoc={quyen.agentChay} />
      ) : (
        <KhongCoQuyen quyen={QUYEN.agentDoc} />
      )}
    </div>
  );
}

function NoiDung({ giaoDuoc, chayDuoc }: { giaoDuoc: boolean; chayDuoc: boolean }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const thoiLuong = useThoiLuong();
  const trangThai = useTrangThaiAgent();
  const tasks = useTaskAgent();
  const [dangXem, setDangXem] = useState<string | null>(null);
  const [huy, setHuy] = useState<string | null>(null);
  const daKetNoi = trangThai.isSuccess && tinhTrangGateway(trangThai.data) === 'daKetNoi';
  const xem = tasks.data?.find((x) => x.id === dangXem);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted" role="status">
        {trangThai.isPending ? (
          <Skeleton className="h-5 w-64" />
        ) : trangThai.isError ? (
          <span>{dichLoi(trangThai.error)}</span>
        ) : (
          <DongKetNoi tt={trangThai.data} />
        )}
      </div>

      <Bento>
        <Tile span="lg:col-span-4">
          <TileHeader icon={<SendOutlined />} tone="violet" title={t('rpaAgent.form.tieuDe')} mo={t('rpaAgent.form.moTa')} />
          <TileBody>
            {!giaoDuoc ? (
              <Empty
                icon={RobotOutlined}
                title={t('rpaAgent.form.khongCoQuyen')}
                description={t('rpaAgent.form.khongCoQuyenMoTa', { quyen: QUYEN.agentTao })}
              />
            ) : trangThai.isPending ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <FormGiaoViec agents={danhSachAgent(trangThai.data)} khoa={!daKetNoi} />
            )}
          </TileBody>
        </Tile>

        <Tile span="lg:col-span-8" className="min-w-0">
          <TileHeader
            icon={<UnorderedListOutlined />}
            title={t('rpaAgent.ds.tieuDe')}
            mo={tasks.isSuccess ? t('rpaAgent.ds.daTai', { so: soVN(tasks.data.length, locale) }) : t('rpaAgent.ds.moTa')}
          />
          {tasks.isPending ? (
            <div className="p-4" aria-busy="true">
              <Skeleton className="h-64 w-full" />
            </div>
          ) : tasks.isError ? (
            <LoiRpa loi={tasks.error} onThuLai={() => void tasks.refetch()} />
          ) : tasks.data.length === 0 ? (
            <Empty icon={RobotOutlined} title={t('rpaAgent.ds.rong')} description={giaoDuoc ? t('rpaAgent.ds.rongMoTa') : undefined} />
          ) : (
            <DataTable<TaskAgent>
              caption={t('rpaAgent.ds.tieuDe')}
              dataSource={tasks.data}
              rowKey="id"
              pagination={tasks.data.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
              scroll={{ x: 'max-content' }}
              columns={[
                { title: t('chung.stt'), key: 'stt', width: 56, render: (_, __, i) => <span className="tabular-nums">{i + 1}</span> },
                {
                  title: t('rpaAgent.ds.cotThoiGian'),
                  key: 'createdAt',
                  width: 150,
                  render: (_, r) => <span className="whitespace-nowrap tabular-nums">{ngayGioVN(r.createdAt, locale)}</span>,
                },
                {
                  title: t('rpaAgent.ds.cotYeuCau'),
                  key: 'prompt',
                  render: (_, r) => (
                    <span className="line-clamp-2 max-w-md text-content-primary" title={r.prompt}>
                      {r.prompt}
                    </span>
                  ),
                },
                { title: t('rpaAgent.ds.cotAgent'), key: 'model', render: (_, r) => r.model },
                {
                  title: t('rpaAgent.ds.cotTrangThai'),
                  key: 'status',
                  render: (_, r) => {
                    const tt = TRANG_THAI_TASK[r.status];
                    return (
                      <Badge variant={tt.variant} className="whitespace-nowrap">
                        {t(tt.khoa)}
                      </Badge>
                    );
                  },
                },
                {
                  title: t('rpaAgent.ds.cotThoiLuong'),
                  key: 'thoiLuong',
                  render: (_, r) =>
                    r.status === 'running' ? (
                      <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
                        {r.elapsedMs === null ? KHONG_CO : t('rpaAgent.ds.daCho', { tg: thoiLuong(r.elapsedMs) })}
                      </span>
                    ) : (
                      <span className="whitespace-nowrap text-xs tabular-nums">{r.latencyMs === null ? KHONG_CO : thoiLuong(r.latencyMs)}</span>
                    ),
                },
                {
                  title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                  key: 'thaoTac',
                  width: 72,
                  align: 'center',
                  fixed: 'right',
                  render: (_, r) => (
                    <MenuThaoTac
                      tenBanGhi={r.prompt.slice(0, 60)}
                      muc={[
                        { khoa: 'xem', nhan: t('rpaAgent.ds.xem'), icon: <EyeOutlined />, nhom: 'xem', onChon: () => setDangXem(r.id) },
                        {
                          khoa: 'huy',
                          nhan: t('rpaAgent.huy.nut'),
                          icon: <StopOutlined />,
                          nhom: 'pha-huy',
                          hien: giaoDuoc && r.status === 'running',
                          onChon: () => setHuy(r.id),
                        },
                      ]}
                    />
                  ),
                },
              ]}
            />
          )}
        </Tile>
      </Bento>

      {xem && <HopChiTietTask task={xem} huyDuoc={giaoDuoc} chayDuoc={chayDuoc} onClose={() => setDangXem(null)} />}
      {huy && <HopHuyTask taskId={huy} onClose={() => setHuy(null)} />}
    </>
  );
}

function DongKetNoi({ tt }: { tt: TrangThaiAgent }) {
  const { t } = useT();
  const g = GATEWAY_HIEN[tinhTrangGateway(tt)];
  return (
    <>
      <Badge variant={g.variant}>{t(g.khoa)}</Badge>
      <span>{g.goiY ? t(g.goiY) : t('rpaAgent.ketNoi.danhSach', { ds: danhSachAgent(tt).join(', ') })}</span>
    </>
  );
}
